import { useEffect, useState } from 'react'
import { useTenantSettings } from '@/features/company/queries'
import { deriveBrandPalette, deriveBrandPaletteForDark, hexToHslTriplet } from '@/lib/color'

/**
 * Mounted once inside any authenticated shell. Pushes the tenant's
 * branding choices onto <html>:
 *
 *   brand_color      → --bk-primary / --bk-primary-hover / --bk-primary-strong
 *                      --bk-accent / --bk-accent-fg / --bk-sidebar-accent
 *   surface_tint     → --bk-bg / --bk-sidebar-bg
 *   card_surface     → --bk-card
 *   text_color       → --bk-text
 *   sidebar_text     → --bk-sidebar-fg
 *   secondary_color  → --bk-secondary
 *
 * NULL on any column clears the inline override on its variable so the
 * tokens.css default (Bokapsys blue + cool gray + white card + dark
 * text + muted sidebar text) takes over again.
 *
 * Status semantics (success/danger/warning/info), borders, and shadows
 * stay fixed on purpose - universal UX patterns we don't want a tenant
 * to break by accident.
 */
const BRAND_VARS = [
  '--bk-primary',
  '--bk-primary-hover',
  '--bk-primary-strong',
  '--bk-accent',
  '--bk-accent-fg',
  '--bk-sidebar-accent',
] as const

const SURFACE_VARS = ['--bk-bg', '--bk-sidebar-bg'] as const

// Each entry: tenant column name → CSS custom property name.
// All four use the same "hex → HSL triplet → setProperty" path.
const SIMPLE_TOKEN_MAP = [
  { col: 'card_surface',    cssVar: '--bk-card' },
  { col: 'text_color',      cssVar: '--bk-text' },
  { col: 'sidebar_text',    cssVar: '--bk-sidebar-fg' },
  { col: 'secondary_color', cssVar: '--bk-secondary' },
] as const

/**
 * Reactive flag that flips to true whenever <html data-theme> becomes
 * "dark", and false otherwise. Inline brand overrides win over the
 * [data-theme='dark'] block in tokens.css (specificity-wise), so we
 * have to actively REMOVE them in dark mode and reapply when the user
 * switches back to light. Without this, tenant-customised installs
 * stay tinted in dark mode and look broken.
 */
function useIsDarkTheme(): boolean {
  const [dark, setDark] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false
    return document.documentElement.getAttribute('data-theme') === 'dark'
  })
  useEffect(() => {
    if (typeof document === 'undefined') return
    const el = document.documentElement
    const obs = new MutationObserver(() => {
      setDark(el.getAttribute('data-theme') === 'dark')
    })
    obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] })
    return () => obs.disconnect()
  }, [])
  return dark
}

export function TenantBrandSync() {
  const { data } = useTenantSettings()
  const tenant = data?.tenant
  const isDark = useIsDarkTheme()

  // Brand identity (--bk-primary + hover) survives in BOTH modes - that's
  // the tenant's signature colour and should persist across the theme
  // toggle. Everything else (surface, card, text, sidebar text,
  // secondary) is lightness-coupled and reverts to the dark defaults so
  // dark mode reads as canonical black/white.
  const brandHex       = tenant?.brand_color ?? null
  const surfaceHex     = isDark ? null : (tenant?.surface_tint ?? null)
  const cardHex        = isDark ? null : (tenant?.card_surface ?? null)
  const textHex        = isDark ? null : (tenant?.text_color ?? null)
  const sidebarTextHex = isDark ? null : (tenant?.sidebar_text ?? null)
  const secondaryHex   = isDark ? null : (tenant?.secondary_color ?? null)

  useEffect(() => {
    const root = document.documentElement
    if (!brandHex) {
      for (const v of BRAND_VARS) root.style.removeProperty(v)
      return
    }
    if (isDark) {
      // Dark mode: only touch the primary stops. Accent + sidebar-accent
      // stay as the dark-default near-black wash from tokens.css; they
      // should NOT inherit a light-derived 95%-lightness wash.
      const palette = deriveBrandPaletteForDark(brandHex)
      if (palette) {
        root.style.setProperty('--bk-primary', palette.primary)
        root.style.setProperty('--bk-primary-hover', palette.hover)
        // primary-strong: keep aligned with primary so hero tiles stay
        // brand-coloured; we don't darken it in dark mode (would vanish
        // against the canvas).
        root.style.setProperty('--bk-primary-strong', palette.primary)
      } else {
        for (const v of BRAND_VARS) root.style.removeProperty(v)
      }
      // Always clear the light-mode accent overrides in dark.
      root.style.removeProperty('--bk-accent')
      root.style.removeProperty('--bk-accent-fg')
      root.style.removeProperty('--bk-sidebar-accent')
      return
    }
    // Light mode: full palette with the pale accent wash.
    const palette = deriveBrandPalette(brandHex)
    if (palette) {
      root.style.setProperty('--bk-primary', palette.primary)
      root.style.setProperty('--bk-primary-hover', palette.hover)
      root.style.setProperty('--bk-primary-strong', palette.strong)
      root.style.setProperty('--bk-accent', palette.accent)
      root.style.setProperty('--bk-accent-fg', palette.accentFg)
      root.style.setProperty('--bk-sidebar-accent', palette.accent)
    } else {
      for (const v of BRAND_VARS) root.style.removeProperty(v)
    }
  }, [brandHex, isDark])

  useEffect(() => {
    const root = document.documentElement
    const triplet = surfaceHex ? hexToHslTriplet(surfaceHex) : null
    if (triplet) {
      root.style.setProperty('--bk-bg', triplet)
      root.style.setProperty('--bk-sidebar-bg', triplet)
    } else {
      for (const v of SURFACE_VARS) root.style.removeProperty(v)
    }
  }, [surfaceHex])

  useEffect(() => {
    // Apply each simple-token override (or clear it) in one pass so we
    // don't end up with stale styles when a tenant flips back to default.
    const root = document.documentElement
    const overrides: Record<string, string | null> = {
      '--bk-card':         cardHex,
      '--bk-text':         textHex,
      '--bk-sidebar-fg':   sidebarTextHex,
      '--bk-secondary':    secondaryHex,
    }
    for (const [cssVar, hex] of Object.entries(overrides)) {
      const triplet = hex ? hexToHslTriplet(hex) : null
      if (triplet) root.style.setProperty(cssVar, triplet)
      else root.style.removeProperty(cssVar)
    }
  }, [cardHex, textHex, sidebarTextHex, secondaryHex])

  // Reference SIMPLE_TOKEN_MAP somewhere so future maintainers grep-find
  // every column ↔ token pair in one place when adding the next theme
  // dimension. (No-op at runtime; tree-shaken in production.)
  useEffect(() => { void SIMPLE_TOKEN_MAP }, [])

  return null
}
